import {
  Box,
  Checkbox,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { Search } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { SidePanelCaption, StandaloneBadge } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';
import type {
  ModalAction,
  ModalActions,
} from 'src/components/ModalContainer/modal-container.types';
import { filterAdornmentSx } from 'src/components/ModalContainer/modal-list.styles';
import { CustomViewAttribute, CustomViewFormModel } from 'src/graphql/entities/custom-view';
import {
  attributeRowSx,
  attributeToggleSx,
} from 'src/pages/CustomViews/custom-views-form-view/draggable-list.styles';
import { capitalizeString } from 'src/utils/strings';
import { DraggableList } from './draggable-list';

interface CustomViewColumnsModalProps {
  open: boolean;
  onClose: () => void;
  attributes: CustomViewAttribute[];
  selectedColumns: CustomViewFormModel['columns'];
  onReorder: (data: CustomViewFormModel['columns']) => void;
  onSelect: (column: CustomViewAttribute) => void;
  /** Shows "Reset to default" in the footer; omitted where the columns have no saved state to go back to. */
  onReset?: () => void;
}

const DisplaySectionHeading = ({ label, count }: { label: string; count?: number }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <SidePanelCaption>{label}</SidePanelCaption>
    {count === undefined ? null : <StandaloneBadge badgeContent={count} size="small" />}
  </Stack>
);

// TODO refactor so this component can be used with react-hook-form
// so it is possible to use it with both raw react handlers and react-hook forms
export const CustomViewsColumnsSelector = ({
  open,
  onClose,
  attributes,
  selectedColumns,
  onSelect,
  onReorder,
  onReset,
}: CustomViewColumnsModalProps) => {
  const [filterString, setFilterString] = useState('');

  const groupedOptions = useMemo(
    () =>
      Object.entries(
        attributes
          ?.filter(
            // filters items which match the search
            (option) =>
              !filterString ||
              option.attributeLabel?.toLowerCase().includes(filterString.toLowerCase()),
          )
          .reduce<Record<string, CustomViewAttribute[]>>(
            // groups matched items by their group
            (acc, i) => ({
              ...acc,
              [i.group]: !acc?.[i.group] ? [i] : [...acc[i.group], i],
            }),
            {},
          ) ?? {},
      ),
    [filterString, attributes],
  );

  const selectedAttributes = useMemo(
    () => new Set(selectedColumns?.map((column) => column.attribute)),
    [selectedColumns],
  );

  const doneAction: ModalAction = { label: 'Done', kind: 'primary', onClick: onClose };
  const actions: ModalActions = onReset
    ? [{ label: 'Reset to default', kind: 'secondary', onClick: onReset }, doneAction]
    : [doneAction];

  return (
    <ModalContainer
      open={open}
      onClose={onClose}
      title="Display"
      size="large"
      fixedHeight
      testId="custom-views-display-modal"
      actions={actions}
    >
      <Stack gap={1.5} mb={3}>
        <DisplaySectionHeading
          label="Visible Attributes"
          count={selectedColumns?.length ?? 0}
        />

        {selectedColumns?.length ? (
          <DraggableList
            selectedColumns={selectedColumns}
            onSelect={onSelect}
            onReorder={onReorder}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Nothing is visible.
          </Typography>
        )}
      </Stack>

      <Stack gap={1.5}>
        <DisplaySectionHeading label="All attributes" />
        <OutlinedInput
          value={filterString}
          onChange={(event) => setFilterString(event.target.value)}
          // The container submits its primary on Enter; filtering should not close the dialog.
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          fullWidth
          placeholder="Filter results"
          inputProps={{ 'aria-label': 'Filter results' }}
          startAdornment={
            <InputAdornment position="start" sx={filterAdornmentSx}>
              <Search aria-hidden />
            </InputAdornment>
          }
        />

        {groupedOptions.length ? (
          groupedOptions.map(([group, options]) => (
            <Stack key={group} gap={0.5} pt={0.5}>
              <SidePanelCaption sx={{ px: 1 }}>{capitalizeString(group)}</SidePanelCaption>
              <Stack>
                {options.map((option) => (
                  <Box key={option.attribute} sx={attributeRowSx}>
                    <Box component="label" sx={attributeToggleSx}>
                      <Checkbox
                        size="small"
                        checked={selectedAttributes.has(option.attribute)}
                        onChange={() => onSelect(option)}
                        sx={{ p: 0 }}
                      />
                      <Typography variant="body2" noWrap minWidth={0}>
                        {option.attributeLabel}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">
            No attribute matches
          </Typography>
        )}
      </Stack>
    </ModalContainer>
  );
};

export const CustomViewsColumnsSelectorForm = memo(CustomViewsColumnsSelector);
